<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="relative z-50" @close="$emit('close')">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                        <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                            <div>
                                <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
                                    <font-awesome-icon :icon="faArrowUp" class="h-6 w-6 text-amber-600" aria-hidden="true" />
                                </div>
                                <div class="mt-3 text-center sm:mt-5">
                                    <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Your Pro plan price will change</DialogTitle>
                                    <div class="mt-3 text-sm text-gray-600">
                                        With this property your plan will be
                                        <span class="font-semibold">{{ formatYearly(change.to_price) }}</span>
                                        from your next renewal<template v-if="renewalDate"> on <span class="font-semibold">{{ renewalDate }}</span></template>
                                        (currently {{ formatYearly(change.from_price) }}).
                                        <span class="font-semibold">Nothing is charged today.</span>
                                    </div>
                                </div>
                            </div>
                            <div class="mt-5 flex sm:mt-6">
                                <button type="button" class="mr-1 w-1/2 flex-shrink-0 justify-center rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200" @click="$emit('close')">Cancel</button>
                                <button type="button" :disabled="processing" class="primaryButton ml-1 w-1/2 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50" @click="$emit('confirm')">{{ processing ? 'Saving…' : 'Continue' }}</button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
// Shown before an account-wide Pro landlord adds, restores or activates the property that
// moves them up a tier (86bcd9hg3, decision 6): the new yearly price applies from the
// next renewal and nothing is charged now. `change` is pro_plan.next_property_tier_change.
import { computed } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons'
import { formatYearly } from '@/Shared/proPlanState'

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    processing: {
        type: Boolean,
        default: false,
    },
    change: {
        type: Object,
        default: () => ({ from_price: 0, to_price: 0, renewal_date: null }),
    },
})

defineEmits(['close', 'confirm'])

const renewalDate = computed(() => props.change?.renewal_date
    ? new Date(`${props.change.renewal_date}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null)
</script>
